import { useEffect } from 'react';
import { BASE_URL, DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE, HOME_TITLE, SEO_KEYWORDS, SITE_NAME } from '@/lib/seo/constants';

const HREFLANG_LOCALES = ['en-GD', 'en-US', 'en-GB', 'en-CA', 'en-TT', 'en'] as const;

export interface SEOConfig {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  schema?: object | object[];
  noindex?: boolean;
  /** Legacy: use title verbatim (no " | ShopGND" suffix). */
  titleAsIs?: boolean;
  keywords?: string;
  type?: 'website' | 'product';
}

/** @deprecated Prefer SEOConfig with required title, description, path */
export interface LegacySEOOptions {
  title?: string;
  titleAsIs?: boolean;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'product';
  noIndex?: boolean;
  path?: string;
  schema?: object | object[];
  ogImage?: string;
}

function normalizePath(path: string): string {
  if (!path || path === '/') return '/';
  const withLeading = path.startsWith('/') ? path : `/${path}`;
  return withLeading.endsWith('/') ? withLeading : `${withLeading}/`;
}

function absolutePageUrl(path: string): string {
  const queryIndex = path.indexOf('?');
  const pathname = queryIndex >= 0 ? path.slice(0, queryIndex) : path;
  const search = queryIndex >= 0 ? path.slice(queryIndex) : '';
  const normalized = normalizePath(pathname);
  if (normalized === '/') return `${BASE_URL}/${search}`;
  return `${BASE_URL}${normalized.replace(/\/$/, '')}/${search}`;
}

function absoluteOgImage(url?: string): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BASE_URL}${url.startsWith('/') ? url : `/${url}`}`;
}

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(name: string, attr: 'name' | 'property' = 'name') {
  document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`)?.remove();
}

function setLink(rel: string, href: string, extra?: Record<string, string>) {
  const selector = extra
    ? `link[rel="${rel}"]${Object.entries(extra)
        .map(([k, v]) => `[${k}="${v}"]`)
        .join('')}`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (extra) {
      for (const [k, v] of Object.entries(extra)) el.setAttribute(k, v);
    }
    document.head.appendChild(el);
  }
  el.href = href;
}

function removeHreflangLinks() {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
}

function removeSchemaScripts() {
  document.head.querySelectorAll('script[data-seo-schema="true"]').forEach((el) => el.remove());
  document.getElementById('site-structured-data')?.remove();
}

function isFullSeoConfig(config: SEOConfig | LegacySEOOptions): config is SEOConfig {
  return (
    typeof (config as SEOConfig).title === 'string' &&
    typeof (config as SEOConfig).description === 'string' &&
    typeof (config as SEOConfig).path === 'string'
  );
}

function toSeoConfig(config: SEOConfig | LegacySEOOptions = {}): {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  schema?: object | object[];
  noindex: boolean;
  titleAsIs: boolean;
  keywords: string;
  type: 'website' | 'product';
} {
  if (isFullSeoConfig(config)) {
    return {
      title: config.title,
      description: config.description,
      path: config.path,
      ogImage: config.ogImage,
      schema: config.schema,
      noindex: config.noindex ?? false,
      titleAsIs: config.titleAsIs ?? true,
      keywords: config.keywords ?? SEO_KEYWORDS,
      type: config.type ?? 'website',
    };
  }
  const legacy = config as LegacySEOOptions;
  const path =
    legacy.path ??
    (legacy.url?.startsWith(BASE_URL)
      ? legacy.url.slice(BASE_URL.length) || '/'
      : typeof window !== 'undefined'
        ? window.location.pathname
        : '/');
  return {
    title: legacy.title ?? HOME_TITLE,
    description: legacy.description ?? DEFAULT_DESCRIPTION,
    path,
    ogImage: legacy.ogImage ?? legacy.image,
    schema: legacy.schema,
    noindex: legacy.noIndex ?? false,
    titleAsIs: legacy.titleAsIs ?? !legacy.title,
    keywords: legacy.keywords ?? SEO_KEYWORDS,
    type: legacy.type ?? 'website',
  };
}

export function useSEO(config: SEOConfig | LegacySEOOptions = {}) {
  const resolved = toSeoConfig(config);
  const {
    title,
    description,
    path,
    ogImage,
    schema,
    noindex,
    titleAsIs,
    keywords,
    type,
  } = resolved;

  const fullTitle = titleAsIs
    ? title
    : title.includes('|')
      ? title
      : title === HOME_TITLE
        ? title
        : `${title} | ${SITE_NAME}`;
  const canonicalUrl = absolutePageUrl(path);
  const schemaKey = schema ? JSON.stringify(schema) : '';

  useEffect(() => {
    const ogImageAbs = absoluteOgImage(ogImage) ?? DEFAULT_OG_IMAGE;

    document.title = fullTitle;
    document.documentElement.lang = 'en';

    setMeta('description', description);
    if (!noindex) setMeta('keywords', keywords);
    else removeMeta('keywords');

    setMeta('robots', noindex ? 'noindex,nofollow' : 'index,follow');

    setLink('canonical', canonicalUrl);

    setMeta('og:title', fullTitle, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('og:site_name', SITE_NAME, 'property');
    setMeta('og:locale', 'en_GD', 'property');
    setMeta('og:type', type, 'property');
    if (ogImage) {
      setMeta('og:image', ogImageAbs, 'property');
    } else {
      removeMeta('og:image', 'property');
    }

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImageAbs);
    else removeMeta('twitter:image');

    setMeta('geo.region', 'GD');

    removeHreflangLinks();
    for (const locale of HREFLANG_LOCALES) {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = locale;
      link.href = canonicalUrl;
      document.head.appendChild(link);
    }

    removeSchemaScripts();
    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      for (const item of schemas) {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-schema', 'true');
        script.textContent = JSON.stringify(item);
        document.head.appendChild(script);
      }
    }

    return () => {
      removeHreflangLinks();
      removeSchemaScripts();
    };
  }, [fullTitle, description, canonicalUrl, ogImage, schemaKey, noindex, keywords, type]);
}
